// @ts-nocheck
//
// Multi-store domain scoping for triggers and product fetchers. Used app-wide
// (trigger dropdowns, PDP / carousel / discount fetchers, product-picker), so
// it lives here in services/helpers rather than under any one feature folder.

import { CHANNELS } from "../../utilities/login.utility";
import botStore from "../../state/store";

type DomainChannelMap = Record<
  string,
  { whatsappPhoneNumberIds: string[]; instagramAccountIds: string[]; emails: string[] }
>;

// The primary (parent) store's channels live under the "_parent_" sentinel key
// in domainChannelMap, but a flow pinned to it carries the primary domain string.
// Map that domain string to the sentinel so lookups resolve to the parent bucket.
export const domainToChannelMapKey = (
  d: string | undefined,
  domainChannelMap?: Record<string, unknown>
): string => {
  if (!d) return "_parent_";
  const primaryDomain = botStore.getState()?.storeInfoState?.partnerStoreDomain;
  if (primaryDomain && d.toLowerCase() === primaryDomain.toLowerCase()) {
    return "_parent_";
  }
  // If the domain isn't a real key but "_parent_" is, the flow is on the parent store.
  if (domainChannelMap && !domainChannelMap[d] && domainChannelMap["_parent_"]) {
    return "_parent_";
  }
  return d;
};

/**
 * True if `channelId` is valid for the flow's saved domain scope.
 *
 * The check compares WHERE THE CHANNEL ACTUALLY LIVES (looked up in
 * domainChannelMap) against the domain the flow saved it under:
 *   - flow not domain-pinned (no domain) → always valid.
 *   - channel not found in any domain → unknown; let hasChannelError decide, so
 *     we return true here (don't double-flag).
 *   - channel found → valid only if its real home matches the flow's domain.
 *
 * The PRIMARY store's domain and the "_parent_" sentinel are treated as the same
 * bucket, since primary-store channels may be filed under either.
 */
export const isChannelInFlowDomain = (
  flowDomain: string | undefined,
  channelId: string,
  domainChannelMap?: DomainChannelMap
): boolean => {
  if (!flowDomain || !domainChannelMap) return true;

  const primaryDomain = botStore.getState()?.storeInfoState?.partnerStoreDomain;
  // Normalize the primary domain and the "_parent_" sentinel to one key.
  const normalize = (d?: string): string => {
    if (!d) return "_parent_";
    if (primaryDomain && d.toLowerCase() === primaryDomain.toLowerCase()) {
      return "_parent_";
    }
    return d.toLowerCase();
  };

  const channelHomeDomain = Object.keys(domainChannelMap).find((mapDomain) => {
    const channels = domainChannelMap[mapDomain];
    return (
      channels.whatsappPhoneNumberIds.includes(channelId) ||
      channels.instagramAccountIds.includes(channelId) ||
      channels.emails.includes(channelId)
    );
  });

  // Not found anywhere → unknown; don't flag here (hasChannelError handles it).
  if (!channelHomeDomain) return true;

  return normalize(channelHomeDomain) === normalize(flowDomain);
};

/**
 * Derives the two "scope" signals a caller must feed into
 * populateChannelIdsDropdown from a flowMeta entry, so every call site computes
 * them identically. A flow is "un-scoped" (should show ALL account channels)
 * when it has no channelSettings AND no existing triggers/keywords.
 */
export const getFlowChannelScope = (
  flowMetaEntry: any
): { channelSettingsIds: string[]; hasExistingTriggers: boolean } => {
  const channelSettingsIds = Object.keys(flowMetaEntry?.channelSettings ?? {});
  const hasExistingTriggers =
    (flowMetaEntry?.triggers?.length ?? 0) > 0 ||
    Object.keys(flowMetaEntry?.keywordsNew ?? {}).length > 0;
  return { channelSettingsIds, hasExistingTriggers };
};

/** Builds the channel dropdown options + selection, scoped to the flow's domain. */
export const populateChannelIdsDropdown = async (
  channel: string,
  channels: any,
  setIsLoading: (value: boolean) => void,
  setSelectedChannel: (obj: any) => void,
  setChannelIdOptions: (options: any) => void,
  currentChannelId?: string,
  domain?: string,
  domainChannelMap?: DomainChannelMap,
  channelSettingsIds?: string[],
  hasExistingTriggers?: boolean
) => {
  setIsLoading(true);

  // 1. Flow has a domain → show that domain's channels.
  // 2. No domain but currentChannelId exists → reverse-lookup owning domain.
  // 3. Not found in any domain → belongs to parent store (_parent_).

  // Fail-safe: a missing domainChannelMap means we can't do domain filtering.
  // Rather than silently rendering an EMPTY dropdown (which looks like a bug to
  // the user and gives a caller no signal it forgot to pass the map), fall back
  // to showing ALL channels — the original, pre-multistore behavior. Callers
  // that DO want domain filtering must pass domainChannelMap.
  const hasDomainChannelMap =
    !!domainChannelMap && Object.keys(domainChannelMap).length > 0;
  if (!hasDomainChannelMap) {
    // eslint-disable-next-line no-console
    console.warn(
      "[populateChannelIdsDropdown] no domainChannelMap — showing all account channels (no domain filtering)"
    );
  }

  // Legacy / un-scoped flow: no domain AND no channelSettings AND no existing
  // triggers means the flow was never pinned to a specific store or channel.
  // In that case show ALL channels available on the account (skip domain
  // filtering entirely). Also show all when we have no domainChannelMap to
  // filter against.
  const showAllChannels =
    !domain &&
    (!hasDomainChannelMap ||
      ((!channelSettingsIds || channelSettingsIds.length === 0) && !hasExistingTriggers));

  let domainFilter: { whatsappPhoneNumberIds: string[]; instagramAccountIds: string[]; emails: string[] } | null = null;
  if (showAllChannels) {
    // No domain and no channelSettings — don't restrict to any domain.
    domainFilter = null;
  } else if (domain) {
    // 1. Flow has an explicit domain — it is authoritative. Show only that
    // domain's channels, or NOTHING if the domain owns none. Do NOT fall
    // through to the currentChannelId reverse-lookup: a stale saved channelId
    // that belongs to a different store must not leak another store's accounts
    // into a domain-scoped flow (icebreakers has no IG → empty selector).
    domainFilter = domainChannelMap?.[domainToChannelMapKey(domain, domainChannelMap)] ?? null;
  } else if (currentChannelId && domainChannelMap) {
    // 2. No domain on flow — reverse-lookup which domain owns this channel ID.
    const ownerDomain = Object.keys(domainChannelMap).find((d) => {
      const entry = domainChannelMap[d];
      return (
        entry.whatsappPhoneNumberIds.includes(currentChannelId) ||
        entry.instagramAccountIds.includes(currentChannelId) ||
        entry.emails.includes(currentChannelId)
      );
    });
    // 3. Not found in any domain — belongs to parent store.
    domainFilter = domainChannelMap[ownerDomain] ?? domainChannelMap["_parent_"] ?? null;
  } else {
    // 3. No domain, no currentChannelId (new trigger on parent store).
    domainFilter = domainChannelMap?.["_parent_"] ?? null;
  }

  // When not showing all channels, a null domainFilter means nothing to show.
  if (!showAllChannels && !domainFilter) {
    setIsLoading(false);
    setChannelIdOptions([]);
    return;
  }

  // Per-channel adapter: where the account list lives on `channels`, how to read
  // an option's {value,label} from an item, and which domainFilter id-list gates
  // it. Everything else (filter → map → select) is channel-agnostic below.
  const ADAPTERS: Record<
    string,
    {
      list: any[];
      value: (item: any) => string;
      label: (item: any) => string;
      allowed: string[];
    }
  > = {
    instagram: {
      list: channels.instagram ?? [],
      value: (i) => i.accountId,
      label: (i) => i.accountName,
      allowed: domainFilter?.instagramAccountIds ?? [],
    },
    whatsapp: {
      list: channels.multiWhatsappInfo ?? [],
      value: (i) => i.phoneNumberId,
      label: (i) => i.accountNumber,
      allowed: domainFilter?.whatsappPhoneNumberIds ?? [],
    },
    email: {
      list: channels.multiChannelEmail ?? [],
      value: (e) => e,
      label: (e) => e,
      allowed: domainFilter?.emails ?? [],
    },
  };

  const adapter = ADAPTERS[channel];
  if (!adapter) {
    setIsLoading(false);
    return;
  }

  const filtered = adapter.list.filter(
    (item: any) => showAllChannels || adapter.allowed.includes(adapter.value(item))
  );

  const options = filtered.map((item: any, i: number) => {
    const value = adapter.value(item);
    // Select the saved channel, or — only for un-scoped flows — auto-pick the
    // first option. A domain-scoped flow with no matching account shows nothing
    // selected (falls through to the error state) rather than defaulting to
    // another store's channel.
    const selected =
      currentChannelId === value ||
      (i === 0 && !currentChannelId && showAllChannels);
    const obj = { label: adapter.label(item), value, selected };
    if (selected) setSelectedChannel(obj);
    return obj;
  });
  setChannelIdOptions(options);
  setIsLoading(false);
};

interface PopulateChannelDropdownForFlowArgs {
  channel: string;
  channels: any;
  setIsLoading: (value: boolean) => void;
  setSelectedChannel: (obj: any) => void;
  setChannelIdOptions: (options: any) => void;
  currentChannelId?: string;
  domain?: string;
  domainChannelMap?: DomainChannelMap;
  /** The flowMeta entry for the flow — scope (channelSettings / triggers) is derived from it. */
  flowMetaEntry: any;
}

/**
 * Caller-facing wrapper around populateChannelIdsDropdown. Takes a single
 * options object and derives the channel "scope" (channelSettingsIds +
 * hasExistingTriggers) from the flowMeta entry via getFlowChannelScope, so no
 * call site has to repeat that boilerplate or the long positional arg list.
 * Prefer this over calling populateChannelIdsDropdown directly.
 */
export const populateChannelIdsDropdownForFlow = (
  args: PopulateChannelDropdownForFlowArgs
) => {
  const { channelSettingsIds, hasExistingTriggers } = getFlowChannelScope(
    args.flowMetaEntry
  );
  return populateChannelIdsDropdown(
    args.channel,
    args.channels,
    args.setIsLoading,
    args.setSelectedChannel,
    args.setChannelIdOptions,
    args.currentChannelId,
    args.domain,
    args.domainChannelMap,
    channelSettingsIds,
    hasExistingTriggers
  );
};

export const getChannelIdName = (
  channel: string,
  channels: any,
  trigger: any
) => {
  let displayName = "";
  if (trigger.channelId && !trigger.isDuplicateChannelId) {
    if (channel === "instagram") {
      displayName = channels?.instagram?.find(
        (channel) => channel.accountId === trigger.channelId
      )?.accountName;
    } else if (channel === "whatsapp") {
      displayName = channels?.multiWhatsappInfo?.find(
        (channel: any) => channel.phoneNumberId === trigger.channelId
      )?.accountNumber;
    } else if (channel === "email") {
      displayName = channels?.multiChannelEmail?.find(
        (email: any) => email === trigger.channelId
      );
    } else if (channel === CHANNELS.LIVECHAT) {
      displayName = "Livechat";
    }
  } else if (
    channel === "instagram" &&
    trigger.instaId &&
    !trigger.isDuplicateChannelId
  ) {
    displayName = channels?.instagram?.find(
      (channel) => channel.accountId === trigger.instaId
    )?.accountName;
  }
  return displayName;
};

export const hasChannelError = (
  channel: string,
  channelId: string,
  channels: any
) => {
  let hasError = false;
  if (!channelId || !channels || Object.keys(channels).length === 0) {
    return hasError;
  }
  if (channel === CHANNELS.INSTAGRAM) {
    const instaChannel = channels?.instagram?.find(
      (channel) => channel.accountId === channelId
    );
    if (!instaChannel) {
      hasError = true;
    }
  } else if (channel === CHANNELS.WHATSAPP) {
    const whatsappChannel = channels?.multiWhatsappInfo?.find(
      (channel: any) => channel.phoneNumberId === channelId
    );
    if (!whatsappChannel) {
      hasError = true;
    }
  } else if (channel === CHANNELS.EMAIL) {
    const emailChannel = channels?.multiChannelEmail?.find(
      (email: any) => email === channelId
    );
    if (!emailChannel) {
      hasError = true;
    }
  } else if (channel === CHANNELS.LIVECHAT) {
    // Live chat doesn't require account connection, so no error
    hasError = false;
  }
  return hasError;
};

/**
 * Given a flowMeta entry for a single flow, extracts the first channelId.
 * Non-keyword triggers store it at triggers[0].channelId.
 * Keyword triggers store the channelId as the key in keywordsNew.
 * channelSettings may contain stale keys from removed channels — only keys
 * that also exist in keywordsNew are considered active.
 */
export const getChannelIdFromFlowMeta = (
  flowMetaEntry: any,
  domainChannelMap?: DomainChannelMap
): string | undefined => {
  // Non-keyword triggers (events, webhooks, etc.)
  const fromTriggers = flowMetaEntry?.triggers?.[0]?.channelId;
  if (fromTriggers) return fromTriggers;
  // Keyword triggers: keywordsNew is the source of truth for active channels
  const keywordsNew = flowMetaEntry?.keywordsNew;
  if (keywordsNew) {
    const activeKeys = Object.keys(keywordsNew).filter((k) => k !== CHANNELS.LIVECHAT);
    if (activeKeys.length === 0) return undefined;
    // If domainChannelMap is available and channels are from mixed stores,
    // prefer the _parent_ store channelId for API calls.
    if (domainChannelMap) {
      const parentIds = [
        ...(domainChannelMap["_parent_"]?.whatsappPhoneNumberIds ?? []),
        ...(domainChannelMap["_parent_"]?.instagramAccountIds ?? []),
        ...(domainChannelMap["_parent_"]?.emails ?? []),
      ];
      const hasParent = activeKeys.some((k) => parentIds.includes(k));
      const hasNonParent = activeKeys.some((k) => !parentIds.includes(k));
      if (hasParent && hasNonParent) {
        // Mixed stores — only use a _parent_ channelId
        return activeKeys.find((k) => parentIds.includes(k));
      }
    }
    return activeKeys[0];
  }
  return undefined;
};

/**
 * Returns the flow's domain, else the domain owning channelId via domainChannelMap.
 *
 * The PRIMARY (parent) store's products/channels live in the plain
 * `store-new/{storeId}` doc, NOT a domain-scoped child doc — so its domain must
 * NEVER be sent to the API (doing so routes to a non-existent child doc → empty
 * result). The parent store's domain is read from storeInfoState.partnerStoreDomain
 * so a flow pinned to it resolves to `undefined` and the API hits the parent doc.
 */
export const resolveEffectiveDomain = (
  domain: string | undefined,
  channelId: string | undefined,
  domainChannelMap: DomainChannelMap | undefined
): string | undefined => {
  const primaryDomain = botStore.getState()?.storeInfoState?.partnerStoreDomain;

  // The parent store's domain must never be sent — return undefined for it.
  const isParentStoreDomain = (domainToCheck?: string): boolean =>
    !!domainToCheck &&
    !!primaryDomain &&
    domainToCheck.toLowerCase() === primaryDomain.toLowerCase();

  // Case 1: the flow has a domain — use it, unless it's the parent store.
  if (domain) {
    return isParentStoreDomain(domain) ? undefined : domain;
  }

  // Case 2: no domain — find which domain owns this channel.
  if (!channelId || !domainChannelMap) return undefined;
  const ownerDomain = Object.keys(domainChannelMap).find((mapDomain) => {
    const channelsForDomain = domainChannelMap[mapDomain];
    return (
      channelsForDomain.whatsappPhoneNumberIds.includes(channelId) ||
      channelsForDomain.instagramAccountIds.includes(channelId) ||
      channelsForDomain.emails.includes(channelId)
    );
  });

  // "_parent_" is a local sentinel for the default store — never send it either.
  if (ownerDomain === "_parent_" || isParentStoreDomain(ownerDomain)) {
    return undefined;
  }
  return ownerDomain;
};
